import RetailManager from 'app/retail_manager/RetailManager';
import Localize from 'app/src/localize/Localize';
import { createReactRenderer } from 'helpers/react-renderer';
import { RenderedComponent as BadgeRenderer } from 'root/apps/PreauthHoldDaysBadge';

import { PreauthButtonRenderer } from './components/PreauthButton.tsx';
import { showPreauthInventoryWarningDialog } from './components/PreauthInventoryWarningDialog';
import TerminalPaymentModal from './components/TerminalPaymentModal';
import { removeInventoryItems } from './utils/preauthActions';
import { getInventorySaleLineIds, hasInventoryItems } from './utils/preauthValidation';

// eslint-disable-next-line
let _preauthModal = null;

export function doPreauthPay({ saleManager, paymentProvider, amount = null, terminal = null }) {
  const terminalCollection = paymentProvider?.terminalCollection || [];
  const hasTerminal = terminalCollection.length > 0;

  if (!hasTerminal) {
    window.alertUser(Localize.translate('No terminal available for preauthorization'), 300);
    return;
  }

  // Check if the sale contains inventory items (not allowed for preauth)
  const hasInventory = hasInventoryItems(saleManager);

  if (hasInventory) {
    const inventoryLineIds = getInventorySaleLineIds(saleManager);

    // Show modal for both cases: all inventory items OR mixed items
    showPreauthInventoryWarningDialog({
      onClose: () => {
        // User chose to close without removing items - do nothing
      },
      onRemoveItems: () => {
        removeInventoryItems(saleManager, inventoryLineIds)
          .then(() => {
            // Redirect to register without form_name to prevent complete_workorder from re-adding the workorder
            window.location.href = '/register.php';
          })
          .catch((error) => {
            window.alertUser(
              Localize.translate('Failed to remove inventory items: ') + (error.message || error),
              300
            );
          });
      },
    });
    return;
  }

  const RenderedComponent = createReactRenderer({
    component: TerminalPaymentModal,
    selector: '[data-react-root="ReactModal"]',
    props: {
      onHide: () => {
        _preauthModal = null;
        RenderedComponent.unmount();
      },
      onPaymentComplete: (payment) =>
        new Promise((resolve, reject) => {
          const status = payment.details?.status;

          // For pre-authorized payments, show the receipt view WITHOUT completing the sale
          // It should remain open, so the pre-auth can be captured or cancelled later
          // Uses the "Save Quote" pattern with register.generic for cleaner separation
          if (status === 'pre-authorized') {
            const contentMap = {
              method: 'POST',
              form_name: 'register.generic',
              register_form: 'preauth_receipt',
              transaction_id: saleManager.sale.id,
              get_receipt: true,
            };

            window.doServerCall(contentMap, (result) => {
              if (result.html) {
                window.merchantos.display.replaceNonAjax(result.html);
                BadgeRenderer.mount();

                // Handle receipt printing if configured
                if (result.receipt_url) {
                  window.merchantos.print.printUrl({
                    url: result.receipt_url,
                    pageType: 'receipt',
                    openCashDrawer: result.openCashDrawer ?? false,
                  });
                } else if (result.openCashDrawer) {
                  window.merchantos.print.openCashDrawer();
                }
              }
              resolve();
            });
          } else if (saleManager.pendingBalance.isZero()) {
            window.merchantos.register
              .completeSale()
              .done(() => {
                resolve();
              })
              .fail(() => {
                reject('Sale not completed');
              });
          } else {
            // still have an outstanding balance for some reason, so
            // take the user to the regular payments screen
            window.merchantos.register.donePay();
            resolve();
          }
        }),
      analyticsName: 'Preauthorization',
      initialAmount: amount ?? saleManager.pendingBalance,
      saleManager,
      paymentProvider,
      autoFinishSale: false,
      captureMethod: 'manual',
      ...(terminal && { terminal }),
    },
  });

  _preauthModal = RenderedComponent.mount();
}

export function setPreauthPayState(saleManager) {
  const hasNoCustomerAttached = saleManager.sale.attributes.customerID <= 0;
  const { saleTotal, pendingPaymentsTotal, deposits } = saleManager;

  // Check if the React component is mounted
  const preauthButtonContainer = document.querySelector('[data-react-root="PreauthButton"]');
  if (!preauthButtonContainer) {
    return false;
  }

  if (hasNoCustomerAttached && window.location.href.includes('register.php')) {
    PreauthButtonRenderer.updateProps({
      disabled: true,
      tooltipText: Localize.translate('Attach a customer to preauthorize'),
    });
    return false;
  }

  if (!RetailManager.active) {
    const preauthDisabled =
      saleTotal.isZero() ||
      !pendingPaymentsTotal.isZero() ||
      !deposits.depositTotal.isZero() ||
      !deposits.refundTotal.isZero();

    if (preauthDisabled) {
      PreauthButtonRenderer.updateProps({
        disabled: true,
        tooltipText: Localize.translate(
          'Preauthorization is not available for this sale. No balance left.'
        ),
      });
      return false;
    }

    PreauthButtonRenderer.updateProps({
      disabled: false,
      tooltipText: '',
    });
  }

  return true;
}
